import React, { useState } from "react";
import { ConfigProvider, Menu } from "antd";
import { Modal } from "antd";
import "./gallery.css";

//-----Use State variable for Gallery filter image and Gallery Category Heading-----------

type GalleryProps = {
  filterImg: string;
  galleryHead: string;
};

const Gallery = ({ filterImg, galleryHead }: GalleryProps) => {
  //--------Gallery Sub Menu Items Array of Objects----------

  const [selected, setSelected] = useState<string>("1");

  const galleryCategories = React.useMemo(
    () => [
      {
        name: "Adult Celebration",
        subMenu: [
          { key: "1", label: "Backdrop Option" },
          { key: "2", label: "Black & Gold" },
          { key: "3", label: "Disco Theme" },
        ],
      },
      {
        name: "Baby Boy",
        subMenu: [
          { key: "1", label: "Barnyard Theme" },
          { key: "2", label: "Bear Theme" },
          { key: "3", label: "Disney Safari Theme" },
          { key: "4", label: "Hot Air Jungle Safari Theme" },
          { key: "5", label: "Jungle Theme" },
          { key: "6", label: "Hot Air Teddy Theme" },
          { key: "7", label: "Peter Rabbit Theme" },
          { key: "8", label: "Premium Carnival Theme" },
          { key: "9", label: "Racing Car Theme" },
          { key: "10", label: "Safari Adventure" },
          { key: "11", label: "Space Theme" },
          { key: "12", label: "Spiderman Theme" },
          { key: "13", label: "Travel & Adventure Theme" },
          { key: "14", label: "Winnie The Pooh" },
        ],
      },
      {
        name: "Baby Girl",
        subMenu: [
          { key: "1", label: "Alice in Wonderland" },
          { key: "2", label: "Barnyard Theme Girl" },
          { key: "3", label: "Basic Fairy Theme" },
          { key: "4", label: "Blush Fairyland" },
          { key: "5", label: "Butterfly Theme" },
          { key: "6", label: "Carnival Theme" },
          { key: "7", label: "Enchanted Garden Theme" },
          { key: "8", label: "Fairy Butterfly Theme" },
          { key: "9", label: "Fairyland" },
          { key: "10", label: "Fairy Butterfly" },
          { key: "11", label: "Fairy Theme" },
          { key: "12", label: "Jungle Theme Girl" },
          { key: "13", label: "Masha & The Bear" },
          { key: "14", label: "Minnie & Mickey" },
          { key: "15", label: "Petals & Pastel Theme" },
          { key: "16", label: "Premium Candy Land Theme" },
          { key: "17", label: "Premium Fairy Land Theme" },
          { key: "18", label: "Princess Theme" },
          { key: "19", label: "Rainbow Theme" },
          { key: "20", label: "Twinkle Twinkle Theme" },
          { key: "21", label: "Under The Sea Theme" },
          { key: "22", label: "Unicorn Fairy Theme" },
          { key: "23", label: "Unicorn Theme" },
          { key: "24", label: "Vintage Carnival" },
          { key: "25", label: "Wonderland" },
        ],
      },
      {
        name: "Baby Shower",
        subMenu: [
          { key: "1", label: "Baby In Bliss" },
          { key: "2", label: "Baby In Bloom" },
          { key: "3", label: "Baby In Bloom Theme" },
          { key: "4", label: "Boho Teddy Theme" },
          { key: "5", label: "Dior Theme" },
          { key: "6", label: "Floral Baby Shower Decor" },
          { key: "7", label: "Jungle Joy" },
          { key: "8", label: "Minnie Elephant Theme" },
          { key: "9", label: "Premium Dior Theme" },
          { key: "10", label: "Tavel Theme" },
          { key: "11", label: "Teddy Theme" },
          { key: "12", label: "Winnie The Pooh Baby Shower" },
        ],
      },
      {
        name: "Bridal Shower",
        subMenu: [
          { key: "1", label: "Bride To Be" },
          { key: "2", label: "Bride To Be Decor" },
          { key: "3", label: "Floral Theme Bride To Be" },
        ],
      },
    ],
    []
  ); // ✅ stable reference

  const selectedCategory = galleryCategories.find(
    (category) => category.name === galleryHead
  );

  const subMenuItems = selectedCategory?.subMenu || [];

  // ✅ Effect: reset subcategory when category changes
  React.useEffect(() => {
    if (selectedCategory?.subMenu?.length) {
      setSelected(selectedCategory.subMenu[0].key);
    }
  }, [galleryHead, selectedCategory]);

  const selectedLabel =
    subMenuItems.find((item) => item.key === selected)?.label || "";

  //--------Gallery Images Modal Open and Close----------

  type GalleryImage = {
    src: string;
    alt: string;
  };

  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const showModal = (image: GalleryImage) => {
    setSelectedImage(image);
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setSelectedImage(null);
  };

  //--------Gallery Images Array of Objects----------

  type GalleryItem = {
    src: string;
    alt: string;
    category: string;
  };

  type Range = {
    start: number;
    end: number;
    ext: string;
  };

  type ThemeConfig = {
    folder: string;
    alt: string;
    ranges: Range[];
  };

  type CategoryConfig = {
    baseFolder: string; // e.g., "adult_celebration" or "baby_girl"
    themes: ThemeConfig[];
  };
  function generateGallery(categories: CategoryConfig[]): GalleryItem[] {
    const items: GalleryItem[] = [];

    categories.forEach((cat) => {
      cat.themes.forEach((theme) => {
        theme.ranges.forEach((range) => {
          for (let i = range.start; i <= range.end; i++) {
            items.push({
              src: `../../images/gallery/${cat.baseFolder}/${theme.folder}/${i}.${range.ext}`,
              alt: theme.alt,
              category: theme.alt,
            });
          }
        });
      });
    });

    return items;
  }

  const configs: CategoryConfig[] = [
    // Adult Celebration
    {
      baseFolder: "adult_celebration",
      themes: [
        {
          folder: "backdrop_options",
          alt: "Backdrop Option",
          ranges: [{ start: 1, end: 33, ext: "jpg" }],
        },
        {
          folder: "black&gold",
          alt: "Black & Gold",
          ranges: [{ start: 1, end: 5, ext: "jpg" }],
        },
        {
          folder: "disco_theme",
          alt: "Disco Theme",
          ranges: [{ start: 1, end: 10, ext: "jpg" }],
        },
      ],
    },
    // Baby Boy
    {
      baseFolder: "baby_boy",
      themes: [
        {
          folder: "barnyard_theme",
          alt: "Barnyard Theme",
          ranges: [{ start: 1, end: 11, ext: "jpg" }],
        },
        {
          folder: "bear_theme",
          alt: "Bear Theme",
          ranges: [{ start: 1, end: 4, ext: "jpeg" }],
        },
        {
          folder: "disney_safari_theme",
          alt: "Disney Safari Theme",
          ranges: [{ start: 1, end: 8, ext: "jpeg" }],
        },
        {
          folder: "Hot-air_Teddy_theme",
          alt: "Hot Air Teddy Theme",
          ranges: [
            { start: 1, end: 1, ext: "png" },
            { start: 2, end: 5, ext: "jpg" },
          ],
        },
        {
          folder: "Hot-Air-+-Jungle-safari",
          alt: "Hot Air Jungle Safari Theme",
          ranges: [{ start: 1, end: 9, ext: "jpeg" }],
        },
        {
          folder: "jungle_theme",
          alt: "Jungle Theme",
          ranges: [
            { start: 1, end: 10, ext: "jpg" },
            { start: 11, end: 21, ext: "jpeg" },
          ],
        },
        {
          folder: "peter_rabbit_theme",
          alt: "Peter Rabbit Theme",
          ranges: [{ start: 1, end: 7, ext: "jpeg" }],
        },
        {
          folder: "premium_carnival_theme",
          alt: "Premium Carnival Theme",
          ranges: [{ start: 1, end: 11, ext: "jpg" }],
        },
        {
          folder: "racing_car",
          alt: "Racing Car Theme",
          ranges: [
            { start: 1, end: 1, ext: "jpg" },
            { start: 2, end: 11, ext: "jpeg" },
          ],
        },
        {
          folder: "safari_adventure",
          alt: "Safari Adventure",
          ranges: [
            { start: 1, end: 1, ext: "jpg" },
            { start: 2, end: 10, ext: "jpeg" },
          ],
        },
        {
          folder: "space_theme",
          alt: "Space Theme",
          ranges: [
            { start: 1, end: 1, ext: "jpg" },
            { start: 2, end: 12, ext: "jpeg" },
          ],
        },
        {
          folder: "spiderman_theme",
          alt: "Spiderman Theme",
          ranges: [{ start: 1, end: 8, ext: "jpeg" }],
        },
        {
          folder: "travel_&_adventure_theme",
          alt: "Travel & Adventure Theme",
          ranges: [{ start: 1, end: 7, ext: "jpeg" }],
        },
        {
          folder: "winnie-the-pooh",
          alt: "Winnie The Pooh",
          ranges: [{ start: 1, end: 11, ext: "jpeg" }],
        },
      ],
    },
    // Baby Girl
    {
      baseFolder: "baby_girl",
      themes: [
        {
          folder: "alice_in_wonderland",
          alt: "Alice in Wonderland",
          ranges: [
            { start: 1, end: 26, ext: "jpg" },
            { start: 27, end: 54, ext: "jpeg" },
          ],
        },
        {
          folder: "barnyard_theme_girl",
          alt: "Barnyard Theme Girl",
          ranges: [{ start: 1, end: 7, ext: "jpg" }],
        },
        {
          folder: "basic_fairy_theme",
          alt: "Basic Fairy Theme",
          ranges: [{ start: 1, end: 11, ext: "jpg" }],
        },
        {
          folder: "blush_fairyland",
          alt: "Blush Fairyland",
          ranges: [
            { start: 1, end: 16, ext: "jpeg" },
            { start: 17, end: 17, ext: "jpg" },
          ],
        },
        {
          folder: "butterfly_theme",
          alt: "Butterfly Theme",
          ranges: [
            { start: 1, end: 18, ext: "jpg" },
            { start: 19, end: 26, ext: "jpeg" },
          ],
        },
        {
          folder: "carnival_theme",
          alt: "Carnival Theme",
          ranges: [
            { start: 1, end: 11, ext: "jpg" },
            { start: 12, end: 16, ext: "jpeg" },
          ],
        },
        {
          folder: "enchanted_garden_theme",
          alt: "Enchanted Garden Theme",
          ranges: [{ start: 1, end: 9, ext: "jpg" }],
        },
        {
          folder: "fairy_butterfly",
          alt: "Fairy Butterfly",
          ranges: [{ start: 1, end: 5, ext: "jpg" }],
        },
        {
          folder: "fairy_butterfly_theme",
          alt: "Fairy Butterfly Theme",
          ranges: [{ start: 1, end: 6, ext: "jpeg" }],
        },
        {
          folder: "fairy_theme",
          alt: "Fairy Theme",
          ranges: [{ start: 1, end: 5, ext: "jpg" }],
        },
        {
          folder: "fairyland",
          alt: "Fairyland",
          ranges: [
            { start: 1, end: 11, ext: "jpg" },
            { start: 12, end: 30, ext: "jpeg" },
          ],
        },
        {
          folder: "jungle_theme_girl",
          alt: "Jungle Theme Girl",
          ranges: [{ start: 1, end: 5, ext: "jpg" }],
        },
        {
          folder: "masha_&_the_bear",
          alt: "Masha & The Bear",
          ranges: [{ start: 1, end: 5, ext: "jpg" }],
        },
        {
          folder: "minnie&mickey",
          alt: "Minnie & Mickey",
          ranges: [{ start: 1, end: 7, ext: "jpeg" }],
        },
        {
          folder: "petals_&_pastel_theme",
          alt: "Petals & Pastel Theme",
          ranges: [{ start: 1, end: 3, ext: "jpg" }],
        },
        {
          folder: "premium_candy_land_theme",
          alt: "Premium Candy Land Theme",
          ranges: [{ start: 1, end: 16, ext: "jpg" }],
        },
        {
          folder: "premium_fairy_land_theme",
          alt: "Premium Fairy Land Theme",
          ranges: [{ start: 1, end: 10, ext: "jpg" }],
        },
        {
          folder: "princess_theme",
          alt: "Princess Theme",
          ranges: [{ start: 1, end: 11, ext: "jpg" }],
        },
        {
          folder: "rainbow_theme",
          alt: "Rainbow Theme",
          ranges: [{ start: 1, end: 6, ext: "jpg" }],
        },
        {
          folder: "twinkle_twinkle_theme",
          alt: "Twinkle Twinkle Theme",
          ranges: [{ start: 1, end: 12, ext: "jpg" }],
        },
        {
          folder: "under_the_sea_theme",
          alt: "Under The Sea Theme",
          ranges: [{ start: 1, end: 10, ext: "jpg" }],
        },
        {
          folder: "unicorn_Fairy_theme",
          alt: "Unicorn Fairy Theme",
          ranges: [{ start: 1, end: 6, ext: "jpg" }],
        },
        {
          folder: "unicorn_theme",
          alt: "Unicorn Theme",
          ranges: [{ start: 1, end: 6, ext: "jpg" }],
        },
        {
          folder: "vintage_carnival",
          alt: "Vintage Carnival",
          ranges: [{ start: 1, end: 12, ext: "jpeg" }],
        },
        {
          folder: "Wonderland",
          alt: "Wonderland",
          ranges: [{ start: 1, end: 7, ext: "jpeg" }],
        },
      ],
    },
    // Baby Shower
    {
      baseFolder: "baby_shower",
      themes: [
        {
          folder: "baby_in_bliss",
          alt: "Baby In Bliss",
          ranges: [{ start: 1, end: 6, ext: "jpeg" }],
        },
        {
          folder: "baby_in_bloom",
          alt: "Baby In Bloom",
          ranges: [{ start: 1, end: 4, ext: "jpg" }],
        },
        {
          folder: "baby_in_bloom_theme",
          alt: "Baby In Bloom Theme",
          ranges: [{ start: 1, end: 10, ext: "jpeg" }],
        },
        {
          folder: "boho_teddy_theme",
          alt: "Boho Teddy Theme",
          ranges: [{ start: 1, end: 9, ext: "jpeg" }],
        },
        {
          folder: "dior_theme",
          alt: "Dior Theme",
          ranges: [
            { start: 1, end: 2, ext: "jpg" },
            { start: 3, end: 17, ext: "jpeg" },
          ],
        },
        {
          folder: "floral_baby_shower_decor",
          alt: "Floral Baby Shower Decor",
          ranges: [{ start: 1, end: 6, ext: "jpg" }],
        },
        {
          folder: "jungle_joy",
          alt: "Jungle Joy",
          ranges: [
            { start: 1, end: 4, ext: "jpg" },
            { start: 5, end: 10, ext: "jpeg" },
            { start: 11, end: 24, ext: "png" },
          ],
        },
        {
          folder: "minnie_elephant_theme",
          alt: "Minnie Elephant Theme",
          ranges: [{ start: 1, end: 10, ext: "jpeg" }],
        },
        {
          folder: "premium_dior_theme",
          alt: "Premium Dior Theme",
          ranges: [
            { start: 1, end: 11, ext: "jpg" },
            { start: 12, end: 21, ext: "jpeg" },
          ],
        },
        {
          folder: "tavel_theme",
          alt: "Tavel Theme",
          ranges: [{ start: 1, end: 8, ext: "jpeg" }],
        },
        {
          folder: "teddy_theme",
          alt: "Teddy Theme",
          ranges: [
            { start: 1, end: 8, ext: "jpg" },
            { start: 9, end: 13, ext: "jpeg" },
          ],
        },
        {
          folder: "winnie_the_pooh_babyShower",
          alt: "Winnie The Pooh Baby Shower",
          ranges: [{ start: 1, end: 5, ext: "jpeg" }],
        },
      ],
    },
    // Bridal Shower
    {
      baseFolder: "bridal_shower",
      themes: [
        {
          folder: "bride_to_be",
          alt: "Bride To Be",
          ranges: [{ start: 1, end: 25, ext: "jpeg" }],
        },
        {
          folder: "bride_to_be_decor",
          alt: "Bride To Be Decor",
          ranges: [{ start: 1, end: 8, ext: "jpeg" }],
        },
        {
          folder: "floral_theme_bride_to_be",
          alt: "Floral Theme Bride To Be",
          ranges: [{ start: 1, end: 9, ext: "jpg" }],
        },
      ],
    },
  ];

  const gallery = generateGallery(configs);

  const myfilteredImages =
    selectedLabel === null
      ? gallery
      : gallery.filter((img) => img.category === selectedLabel);
  return (
    <>
      <div className="gallery">
        <div className="container">
          <h2>{galleryHead} </h2>
          <ConfigProvider
            theme={{
              token: {
                colorPrimary: "#020202", // Main theme color
                colorBgContainer: "#fff", // Background
                colorText: "#000", // Text color
              },
              components: {
                Menu: {
                  itemHoverColor: "#020202", // Hover text color
                  itemSelectedColor: "#020202", // Selected text color
                  itemSelectedBg: "#d8d8d8ff", // Selected background color
                },
              },
            }}
          >
            <Menu
              mode="horizontal"
              selectedKeys={[selected]}
              onClick={(e) => setSelected(e.key)}
              items={subMenuItems}
            />
          </ConfigProvider>

          <div className="gallery__images">
            {/* --------Gallery Images for Map function---------- */}

            {myfilteredImages.map((image, index) => (
              <img
                key={index}
                src={image.src}
                alt={image.alt}
                style={{ cursor: "pointer" }}
                onClick={() => showModal(image)}
                loading="lazy"
              />
            ))}
            <Modal
              title={selectedImage?.alt || "Image Preview"}
              open={isModalOpen}
              onCancel={handleCancel}
              footer={null} // remove default footer buttons
              width="auto"
            >
              {selectedImage && (
                <img
                  src={selectedImage.src}
                  alt={selectedImage.alt}
                  // style={{ width: "100%", height: "auto" }}
                />
              )}
            </Modal>
          </div>
        </div>
      </div>
    </>
  );
};

export default Gallery;
